import { useMutation } from "@tanstack/react-query";
import { llmService } from "../services/llmService";
import { useNewton } from "../store/NewtonContext";
import { useInvalidate } from "../../../../../hooks/useInvalidate";

/**
 * Usage:
 * const { mutate: deleteSession, isPending } = useLlmSessionDelete({
 *   userId: userData?.uid,
 *   onSuccess: () => setSessionId(null),
 * })
 */
export function useLlmSessionDelete({ userId, onSuccess } = {}) {
  const { llmEnv } = useNewton();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: async ({ sessionId }) => {
      const res = await llmService.deleteSession({
        env: llmEnv,
        sessionId,
      });

      if (!res.ok) {
        throw new Error(`Failed to delete session: ${res.status}`);
      }

      const responseText = await res.text();
      if (!responseText) {
        throw new Error("Empty response from delete session");
      }

      // Parse the JSON string response as per API spec
      try {
        const parsedResponse = JSON.parse(responseText);
        return parsedResponse;
      } catch (error) {
        throw new Error("Failed to parse delete session response");
      }
    },
    onSuccess: async (...args) => {
      try {
        if (typeof onSuccess === "function") onSuccess(...args);

        if (userId && llmEnv) {
          invalidate(["getLlmSessions_key"]);
        }
      } catch (err) {
        console.error("useLlmSessionDelete onSuccess error:", err);
      }
    },
  });
}
